import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import cx from "classnames";
import { convertDatesToTimezone } from "services/report.service";
import { CSVLink } from "react-csv";
import dateformat from "dateformat";
import { Button } from "react-bootstrap";
import { padNumber } from "services/util";

import { TableFooterInfo, TableHeaderInfo } from "..";
import { EmptyMessage, LoadingMessage } from "../../reports/components";
import { fetchAssetsAnalyticsForDownload, fetchingWorkspaceAssetAnalytics, invalidateAssetAnalyticsDownloadData } from "actions/workspaceReports";
import { assetAnalyticsHeader } from "constants/workspaceReportsData";
import { AssetDetails, StoryboardDetails } from ".";

import styles from '../workspaceReports.module.scss'

export default function WorkspaceAssetAnalytics({ date, contentType, modalView = false, sectionId = "" }) {
  const dispatch = useDispatch();

  const [currentChart, setCurrentChart] = useState("table_view");
  const loading = useSelector(state => _.get(state, `workspaces.reports.assets.loading`, false));
  const data = useSelector(state => _.get(state, `workspaces.reports.assets.items`, []));
  const pagination = useSelector(state => _.get(state, 'workspaces.reports.assets.pagination', {}));
  const totalCount = pagination.totalRecordsCount || 0;

  useEffect(() => {
    if (date || (contentType && contentType.length > 0)) {
      handlePageChange(0);
    }
  }, [date, contentType]);

  const handlePageChange = (page) => {
    handleAssetAnalytics(page);
  }

  const handleAssetAnalytics = (page) => {
    if (pagination.current != -1) {
      const request = {
        types: contentType,
        ...convertDatesToTimezone(date),
      }

      dispatch(fetchingWorkspaceAssetAnalytics(request, page, 20));
    }
  }

  useEffect(() => {
    if (pagination.current == -1) {
      const request = {
        types: contentType,
        ...convertDatesToTimezone(date),
      }
      dispatch(fetchingWorkspaceAssetAnalytics(request, 0, 20));
    }
  }, [pagination]);

  return (
    <div className={cx(styles.storyboardReportsWrapper, { [styles.modalWrapper]: modalView })} id={sectionId}>
      <div className={styles.storyboardReportsContainer}>
        <TableHeaderInfo title="Asset Analytics" count={totalCount || 0} modalView={modalView} />
        <>
          <AssetAnalyticsReports
            data={data}
            modalView={modalView}
            date={date}
            count={totalCount}
            type={contentType}
            loading={loading}
          />
        </>

        {/* {currentChart == "graph_view" &&
                        <div className={styles.graphView}>Graph view</div>
                    } */}
      </div>
      <TableFooterInfo
          data={data}
          currentChart={currentChart}
          disableView={['pie_chart_view']}
          modalView={modalView}
          onChange={handlePageChange}
          type='assetAnalytics'
          pagination={pagination}
          paginationName={"Assets"}
          date={date}
          contentType={contentType}
        />
    </div>
  )
}

export const AssetAnalyticsReports = ({ data, modalView, date, type, loading }) => {
  const assetsData = useSelector(state => {
    const records = Array.isArray(data) ? data : [];
    return modalView ? records : records.slice(0, 5);
  });

  const _renderTimeSpent = (seconds) => {
    let minutes = padNumber(Math.floor(seconds / 60), 2);
    seconds = padNumber(Math.floor(seconds % 60), 2);
    var hours = padNumber(Math.floor(minutes / 60), 2);
    minutes = padNumber(minutes % 60, 2);
    hours = hours > 0 ? `${hours}:` : ''
    return `${hours}${minutes}:${seconds}`;
  }

  return (
    <>
      <div className={cx(styles.storyboardReportsView, { [styles.tableDataOverflow]: modalView })}>
        <div className={cx(styles.tableHeaderWrapper, { [styles.sticky]: modalView })}>
          <div className={styles.downloadOption}>
            <ExportCSV date={date} type={type} />
          </div>
        </div>
        <table className={styles.assetAnalyticsTable}>
          <thead className={cx(styles.tableHeader, { [styles.stickyHeader]: modalView })}>
            {assetAnalyticsHeader.map((header) => {
              return <th className={styles.headerData}>{header}</th>
            })}
          </thead>

          {loading &&
            <div className={cx(styles.loader, { [styles.modalLoader]: modalView })}>
              <LoadingMessage className={styles.loadingMessage} />
            </div>
          }
          {!loading && data.length == 0 &&
            <div className={cx(styles.loader, { [styles.modalLoader]: modalView })}>
              <EmptyMessage />
            </div>
          }

          {!loading && data && data.length > 0 &&
            <tbody className={styles.tableBody}>
              {assetsData.map((data) => (
                <>
                  <tr className={styles.tableBodyDataRow}>
                    <td className={styles.tableBodyData}>
                      <AssetDetails data={data} />
                    </td>
                    <td className={styles.tableBodyData}>
                      <StoryboardDetails data={data} />
                    </td>
                    <td className={styles.tableBodyData}>
                      <div className={styles.views}>
                        {data.views}
                      </div>
                    </td>
                    <td className={styles.tableBodyData}>
                      <div className={styles.visitors}>
                        {data.visitors}
                      </div>
                    </td>
                    <td className={styles.tableBodyData}>
                      <div className={styles.timeSpent}>
                        {data.timeSpent == 0 ? "00:00" : _renderTimeSpent(data.timeSpent / data.views)}
                      </div>
                    </td>

                    <td className={styles.tableBodyData}>
                      <div className={styles.timeSpent}>
                        {(data.bounceRate * 100 / data.views).toFixed(0)}%
                      </div>
                    </td>

                    <td className={styles.tableBodyData}>
                      <div className={styles.timeSpent}>
                        {data.downloads}
                      </div>
                    </td>
                  </tr>
                </>
              ))}
            </tbody>
          }
        </table>
      </div>
    </>
  )
}


function ExportCSV({ date, type }) {
  const dispatch = useDispatch();

  const downloads = useSelector(state => _.get(state, 'workspaces.download.assets.items', []));
  const completed = useSelector(state => _.get(state, 'workspaces.download.assets.completed'));
  const progress = useSelector(state => _.get(state, 'workspaces.download.assets.progress'));
  const pagination = useSelector(state => _.get(state, 'workspaces.download.assets.pagination', {}));
  const [csvData, setCsvData] = useState([]);
  const csvLink = useRef();

  const headers = [
    { label: "Asset Name", key: "assetName" },
    { label: "Asset Type", key: "assetType" },
    { label: "Storyboard Name", key: "storyboardName" },
    { label: "Content-Type", key: "contentType" },
    { label: "Last Visited", key: "lastVisitedDate" },
    { label: "Views", key: "views" },
    { label: "Visitors", key: "visitors" },
    { label: "Avg Time Spent", key: "avgTimeSpent" },
    { label: "Bounce Rate", key: "bounceRate" },
    { label: "Downloads", key: "downloads" }
  ];

  useEffect(() => {
    if (completed) {
      var columns = [];
      downloads && downloads.map((data, index) => {

        const _renderTimeSpent = (seconds) => {
          let minutes = padNumber(Math.floor(seconds / 60), 2);
          seconds = padNumber(Math.floor(seconds % 60), 2);
          var hours = padNumber(Math.floor(minutes / 60), 2);
          minutes = padNumber(minutes % 60, 2);

          hours = hours > 0 ? `${hours}:` : ''
          return `${hours}${minutes}:${seconds}`;
        }
        const _renderBounceRate = (value) => {
          let bounceRate = (value * 100 / data.views).toFixed(0);
          return bounceRate + "%";
        }

        columns[index] = {
          assetName: _.get(data, `asset.content.asset.name`, "-"),
          assetType: _.get(data, `asset.content.asset.metadata.content_type`, "-"),
          storyboardName: _.get(data, `storyboardResponse.name`, "-"),
          contentType: _.get(data, `storyboardResponse.type`, "-"),
          lastVisitedDate: _.get(data, `lastPublished`, "-") == undefined ? "" : dateformat(_.get(data, `lastPublished`, ""), 'dd mmm yyyy'),
          views: _.get(data, `views`, "-"),
          visitors: _.get(data, `visitors`, "-"),
          avgTimeSpent: _.get(data, `timeSpent`) == 0 ? "00:00" : _renderTimeSpent(_.get(data, `timeSpent`) / _.get(data, `views`)),
          bounceRate: _renderBounceRate(_.get(data, `bounceRate`, "")),
          downloads: _.get(data, `downloads`, ""),
        }
      });

      setCsvData(columns);
    }
  }, [completed]);

  useEffect(() => {
    if (!completed && csvData && csvData.length == 0) {
      return;
    }

    csvLink.current.link.click();
    dispatch(invalidateAssetAnalyticsDownloadData());
  }, [csvData]);

  const fetchLeadReportData = () => {
    dispatch(invalidateAssetAnalyticsDownloadData());
    const request = {
      types: type,
      ...convertDatesToTimezone(date),
    }
    dispatch(fetchAssetsAnalyticsForDownload(request, 0, 200))
  }

  return (
    <div>
      <Button variant="transparent" disabled={progress} onClick={() => fetchLeadReportData()} className={styles.download}>
        <i className={cx("cleverstory-download", styles.downloadIcon)} />
        <span className={styles.progress}>{progress ? "Downloading..." : "Download "}</span>
      </Button>
      <CSVLink
        ref={csvLink}
        headers={headers}
        data={csvData}
        filename='assetAnalyticsReports.csv'
        className='hidden'
      />
    </div>
  )
}